import { SupabaseClient } from '@supabase/supabase-js';

// ==================== TYPES ====================

export type ModuleEventType = 'create' | 'update' | 'delete' | 'upload';

export type ModuleName = 
  | 'project_inventory' | 'project_vehicles' | 'project_insurances'
  | 'project_contracts' | 'project_vendors' | 'project_documents'
  | 'bookings' | 'contacts' | 'marketing_campaigns' | 'marketing_expenses'
  | 'marketing_ads' | 'files' | 'calendar_events' | 'project_bank_accounts';

export interface ModuleEvent {
  type: ModuleEventType;
  module: ModuleName;
  data: any;
  projectId?: string;
}

export type WarningSeverity = 'info' | 'warning' | 'critical';

export type WarningAction = 
  | 'create_booking' | 'create_insurance' | 'create_vendor' 
  | 'create_contract' | 'link_document' | 'create_contact';

export interface ModuleWarning {
  id: string;
  module: ModuleName;
  relatedModule: ModuleName;
  message: string;
  severity: WarningSeverity;
  action?: WarningAction;
  actionLabel?: string;
  projectId?: string;
  recordId?: string;
  recordName?: string;
}

export interface IntegrityIssue {
  id: string;
  module: string;
  problem: string;
  severity: WarningSeverity;
  projectId?: string;
  projectName?: string;
  recordId?: string;
  recordName?: string;
  autoFixable: boolean;
  fixAction?: WarningAction;
  fixLabel?: string;
}

// ==================== DEPENDENCY MAP ====================

interface DependencyRule {
  targetModule: ModuleName;
  description: string;
  checkFn?: string; // name of check function
}

export const DEPENDENCY_MAP: Record<string, DependencyRule[]> = {
  project_inventory: [
    { targetModule: 'bookings', description: 'Kaufpreis → Finanzbuchung' },
    { targetModule: 'project_documents', description: 'Kaufbeleg / Garantie' },
    { targetModule: 'project_vendors', description: 'Hersteller / Lieferant' },
  ],
  project_vehicles: [
    { targetModule: 'project_insurances', description: 'KFZ-Versicherung' },
    { targetModule: 'bookings', description: 'Kaufpreis / Wartungskosten' },
    { targetModule: 'project_documents', description: 'Fahrzeugschein / TÜV' },
  ],
  project_insurances: [
    { targetModule: 'bookings', description: 'Versicherungsprämien → Finanzbuchung' },
    { targetModule: 'project_contracts', description: 'Versicherungsvertrag' },
  ],
  project_contracts: [
    { targetModule: 'project_vendors', description: 'Vertragspartner' },
    { targetModule: 'bookings', description: 'Vertragswert → Zahlungsplan' },
    { targetModule: 'project_documents', description: 'Vertragsdokument' },
  ],
  project_vendors: [
    { targetModule: 'contacts', description: 'Kontaktperson als CRM-Eintrag' },
    { targetModule: 'project_contracts', description: 'Zugehörige Verträge' },
  ],
  marketing_campaigns: [
    { targetModule: 'bookings', description: 'Budget / Ausgaben → Finanzen' },
  ],
  marketing_expenses: [
    { targetModule: 'bookings', description: 'Ausgabe → Finanzbuchung' },
  ],
  bookings: [
    { targetModule: 'project_documents', description: 'Beleg / Rechnung' },
  ],
};

// ==================== MODULE LABELS ====================

export const MODULE_LABELS: Record<string, string> = {
  project_inventory: 'Inventar',
  project_vehicles: 'Fahrzeuge',
  project_insurances: 'Versicherungen',
  project_contracts: 'Verträge',
  project_vendors: 'Anbieter',
  project_documents: 'Dokumente',
  bookings: 'Finanzbuchungen',
  contacts: 'Kontakte',
  marketing_campaigns: 'Marketing-Kampagnen',
  marketing_expenses: 'Marketing-Ausgaben',
  marketing_ads: 'Werbeanzeigen',
  files: 'Dateien',
  calendar_events: 'Kalender',
  project_bank_accounts: 'Bankkonten',
};

// ==================== HELPER FUNCTIONS ====================

export function getAffectedModules(module: string): string[] {
  const deps = DEPENDENCY_MAP[module];
  if (!deps) return [];
  return deps.map(d => d.targetModule);
}

export function getModuleLabel(module: string): string {
  return MODULE_LABELS[module] || module;
}

// ==================== UPLOAD INTELLIGENCE ====================

const UPLOAD_KEYWORDS: Record<string, ModuleName[]> = {
  'rechnung': ['bookings'],
  'invoice': ['bookings'],
  'quittung': ['bookings'],
  'beleg': ['bookings'],
  'vertrag': ['project_contracts'],
  'contract': ['project_contracts'],
  'versicherung': ['project_insurances'],
  'police': ['project_insurances'],
  'fahrzeug': ['project_vehicles'],
  'kfz': ['project_vehicles'],
  'inventar': ['project_inventory'],
  'garantie': ['project_inventory'],
  'lieferschein': ['project_vendors'],
  'angebot': ['project_vendors', 'project_contracts'],
};

export function detectUploadModules(fileName: string): ModuleName[] {
  const lower = fileName.toLowerCase();
  const detected: Set<ModuleName> = new Set();
  
  for (const [keyword, modules] of Object.entries(UPLOAD_KEYWORDS)) {
    if (lower.includes(keyword)) {
      modules.forEach(m => detected.add(m));
    }
  }
  
  return Array.from(detected);
}

// ==================== INTEGRITY CHECK ====================

export async function systemIntegrityCheck(
  supabase: SupabaseClient
): Promise<IntegrityIssue[]> {
  const issues: IntegrityIssue[] = [];
  let issueCounter = 0;
  const nextId = () => `issue-${++issueCounter}`;

  try {
    // Fetch all projects first
    const { data: projects } = await supabase.from('projects').select('id, name');
    if (!projects || projects.length === 0) return issues;

    // Batch fetch all relevant data
    const [
      { data: inventory },
      { data: vehicles },
      { data: insurances },
      { data: contracts },
      { data: vendors },
      { data: bookings },
      { data: documents },
    ] = await Promise.all([
      (supabase as any).from('project_inventory').select('id, item_name, project_id, purchase_price'),
      supabase.from('project_vehicles').select('id, brand, model, project_id, purchase_price, insurance_provider'),
      supabase.from('project_insurances').select('id, insurance_type, provider, project_id, premium_monthly, premium_yearly'),
      supabase.from('project_contracts').select('id, title, project_id, vendor_id, value'),
      supabase.from('project_vendors').select('id, name, project_id'),
      supabase.from('bookings').select('id, description, project_id, type, amount, category'),
      supabase.from('project_documents').select('id, name, project_id, related_contract_id, related_vendor_id'),
    ]);

    const projectMap = new Map(projects.map(p => [p.id, p.name]));

    // 1. Inventar ohne Kaufbuchung
    if (inventory) {
      for (const item of inventory) {
        if (item.purchase_price && item.purchase_price > 0) {
          const hasBooking = bookings?.some(
            b => b.project_id === item.project_id && 
                 b.description?.toLowerCase().includes(item.item_name?.toLowerCase())
          );
          if (!hasBooking) {
            issues.push({
              id: nextId(),
              module: 'Inventar',
              problem: `"${item.item_name}" hat Kaufpreis (${item.purchase_price}€) aber keine Finanzbuchung`,
              severity: 'warning',
              projectId: item.project_id,
              projectName: projectMap.get(item.project_id),
              recordId: item.id,
              recordName: item.item_name,
              autoFixable: true,
              fixAction: 'create_booking',
              fixLabel: 'Buchung erstellen',
            });
          }
        }
      }
    }

    // 2. Fahrzeuge ohne Versicherung
    if (vehicles) {
      for (const vehicle of vehicles) {
        if (!vehicle.insurance_provider) {
          const hasInsurance = insurances?.some(
            i => i.project_id === vehicle.project_id && 
                 i.insurance_type?.toLowerCase().includes('kfz')
          );
          if (!hasInsurance) {
            issues.push({
              id: nextId(),
              module: 'Fahrzeuge',
              problem: `"${vehicle.brand} ${vehicle.model || ''}" hat keine Versicherung`,
              severity: 'critical',
              projectId: vehicle.project_id,
              projectName: projectMap.get(vehicle.project_id),
              recordId: vehicle.id,
              recordName: `${vehicle.brand} ${vehicle.model || ''}`.trim(),
              autoFixable: false,
              fixAction: 'create_insurance',
              fixLabel: 'Versicherung anlegen',
            });
          }
        }

        // Fahrzeug mit Kaufpreis ohne Buchung
        if (vehicle.purchase_price && vehicle.purchase_price > 0) {
          const hasBooking = bookings?.some(
            b => b.project_id === vehicle.project_id &&
                 b.description?.toLowerCase().includes(vehicle.brand?.toLowerCase())
          );
          if (!hasBooking) {
            issues.push({
              id: nextId(),
              module: 'Fahrzeuge',
              problem: `"${vehicle.brand} ${vehicle.model || ''}" hat Kaufpreis (${vehicle.purchase_price}€) aber keine Finanzbuchung`,
              severity: 'warning',
              projectId: vehicle.project_id,
              projectName: projectMap.get(vehicle.project_id),
              recordId: vehicle.id,
              recordName: `${vehicle.brand} ${vehicle.model || ''}`.trim(),
              autoFixable: true,
              fixAction: 'create_booking',
              fixLabel: 'Buchung erstellen',
            });
          }
        }
      }
    }

    // 3. Verträge ohne Anbieter
    if (contracts) {
      for (const contract of contracts) {
        if (!contract.vendor_id) {
          issues.push({
            id: nextId(),
            module: 'Verträge',
            problem: `"${contract.title}" hat keinen zugewiesenen Anbieter`,
            severity: 'warning',
            projectId: contract.project_id,
            projectName: projectMap.get(contract.project_id),
            recordId: contract.id,
            recordName: contract.title,
            autoFixable: false,
            fixAction: 'create_vendor',
            fixLabel: 'Anbieter zuweisen',
          });
        }

        // Vertrag mit Wert ohne Buchung
        if (contract.value && contract.value > 0) {
          const hasBooking = bookings?.some(
            b => b.project_id === contract.project_id &&
                 b.description?.toLowerCase().includes(contract.title?.toLowerCase())
          );
          if (!hasBooking) {
            issues.push({
              id: nextId(),
              module: 'Verträge',
              problem: `"${contract.title}" hat Vertragswert (${contract.value}€) aber keine Finanzbuchung`,
              severity: 'info',
              projectId: contract.project_id,
              projectName: projectMap.get(contract.project_id),
              recordId: contract.id,
              recordName: contract.title,
              autoFixable: true,
              fixAction: 'create_booking',
              fixLabel: 'Buchung erstellen',
            });
          }
        }
      }
    }

    // 4. Versicherungen ohne Zahlungseintrag
    if (insurances) {
      for (const insurance of insurances) {
        if ((insurance.premium_monthly && insurance.premium_monthly > 0) || 
            (insurance.premium_yearly && insurance.premium_yearly > 0)) {
          const hasBooking = bookings?.some(
            b => b.project_id === insurance.project_id &&
                 (b.category?.toLowerCase().includes('versicherung') ||
                  b.description?.toLowerCase().includes(insurance.provider?.toLowerCase()))
          );
          if (!hasBooking) {
            const amount = insurance.premium_yearly || (insurance.premium_monthly! * 12);
            issues.push({
              id: nextId(),
              module: 'Versicherungen',
              problem: `"${insurance.insurance_type}" bei ${insurance.provider} hat Prämie (${amount}€/Jahr) aber keine Finanzbuchung`,
              severity: 'warning',
              projectId: insurance.project_id,
              projectName: projectMap.get(insurance.project_id),
              recordId: insurance.id,
              recordName: `${insurance.insurance_type} - ${insurance.provider}`,
              autoFixable: true,
              fixAction: 'create_booking',
              fixLabel: 'Buchung erstellen',
            });
          }
        }
      }
    }

    // 5. Dokumente ohne Modulzuordnung
    if (documents) {
      for (const doc of documents) {
        if (!doc.related_contract_id && !doc.related_vendor_id) {
          issues.push({
            id: nextId(),
            module: 'Dokumente',
            problem: `"${doc.name}" ist keinem Vertrag oder Anbieter zugeordnet`,
            severity: 'info',
            projectId: doc.project_id,
            projectName: projectMap.get(doc.project_id),
            recordId: doc.id,
            recordName: doc.name,
            autoFixable: false,
            fixAction: 'link_document',
            fixLabel: 'Verknüpfen',
          });
        }
      }
    }

    // 6. Anbieter ohne Verträge
    if (vendors && contracts) {
      for (const vendor of vendors) {
        const hasContract = contracts.some(c => c.vendor_id === vendor.id);
        if (!hasContract) {
          issues.push({
            id: nextId(),
            module: 'Anbieter',
            problem: `"${vendor.name}" hat keine zugewiesenen Verträge`,
            severity: 'info',
            projectId: vendor.project_id,
            projectName: projectMap.get(vendor.project_id),
            recordId: vendor.id,
            recordName: vendor.name,
            autoFixable: false,
            fixAction: 'create_contract',
            fixLabel: 'Vertrag erstellen',
          });
        }
      }
    }

  } catch (error) {
    console.error('Integrity check error:', error);
  }

  return issues;
}

// ==================== CROSS-CHECK ON EVENT ====================

export async function crossCheckAfterEvent(
  supabase: SupabaseClient,
  event: ModuleEvent
): Promise<ModuleWarning[]> {
  const warnings: ModuleWarning[] = [];
  const affected = getAffectedModules(event.module);
  
  if (affected.length === 0) return warnings;

  const projectId = event.projectId || event.data?.project_id;
  if (!projectId) return warnings;

  let warnCounter = 0;
  const nextId = () => `warn-${Date.now()}-${++warnCounter}`;

  try {
    // For each affected module, check if relations exist
    for (const target of affected) {
      switch (`${event.module}→${target}`) {
        case 'project_inventory→bookings': {
          if (event.data?.purchase_price > 0) {
            const { count } = await supabase
              .from('bookings')
              .select('id', { count: 'exact', head: true })
              .eq('project_id', projectId)
              .ilike('description', `%${event.data.item_name || ''}%`);
            if (!count || count === 0) {
              warnings.push({
                id: nextId(),
                module: event.module,
                relatedModule: 'bookings',
                message: `Inventar "${event.data.item_name}" hat Kaufpreis aber keine Finanzbuchung`,
                severity: 'warning',
                action: 'create_booking',
                actionLabel: 'Buchung erstellen',
                projectId,
                recordId: event.data.id,
                recordName: event.data.item_name,
              });
            }
          }
          break;
        }

        case 'project_vehicles→project_insurances': {
          if (!event.data?.insurance_provider) {
            const { count } = await supabase
              .from('project_insurances')
              .select('id', { count: 'exact', head: true })
              .eq('project_id', projectId)
              .ilike('insurance_type', '%kfz%');
            if (!count || count === 0) {
              warnings.push({
                id: nextId(),
                module: event.module,
                relatedModule: 'project_insurances',
                message: `Fahrzeug "${event.data.brand} ${event.data.model || ''}" hat keine Versicherung`,
                severity: 'critical',
                action: 'create_insurance',
                actionLabel: 'Versicherung anlegen',
                projectId,
                recordId: event.data.id,
                recordName: `${event.data.brand} ${event.data.model || ''}`.trim(),
              });
            }
          }
          break;
        }

        case 'project_vehicles→bookings': {
          if (event.data?.purchase_price > 0) {
            const { count } = await supabase
              .from('bookings')
              .select('id', { count: 'exact', head: true })
              .eq('project_id', projectId)
              .ilike('description', `%${event.data.brand || ''}%`);
            if (!count || count === 0) {
              warnings.push({
                id: nextId(),
                module: event.module,
                relatedModule: 'bookings',
                message: `Fahrzeug "${event.data.brand}" hat Kaufpreis aber keine Buchung`,
                severity: 'warning',
                action: 'create_booking',
                actionLabel: 'Buchung erstellen',
                projectId,
                recordId: event.data.id,
              });
            }
          }
          break;
        }

        case 'project_insurances→bookings': {
          const premium = event.data?.premium_yearly || (event.data?.premium_monthly ? event.data.premium_monthly * 12 : 0);
          if (premium > 0) {
            const { count } = await supabase
              .from('bookings')
              .select('id', { count: 'exact', head: true })
              .eq('project_id', projectId)
              .ilike('description', `%${event.data.provider || ''}%`);
            if (!count || count === 0) {
              warnings.push({
                id: nextId(),
                module: event.module,
                relatedModule: 'bookings',
                message: `Versicherung "${event.data.insurance_type}" bei ${event.data.provider} hat Prämie aber keine Buchung`,
                severity: 'warning',
                action: 'create_booking',
                actionLabel: 'Buchung erstellen',
                projectId,
                recordId: event.data.id,
              });
            }
          }
          break;
        }

        case 'project_contracts→project_vendors': {
          if (!event.data?.vendor_id) {
            warnings.push({
              id: nextId(),
              module: event.module,
              relatedModule: 'project_vendors',
              message: `Vertrag "${event.data.title}" hat keinen Anbieter`,
              severity: 'warning',
              action: 'create_vendor',
              actionLabel: 'Anbieter zuweisen',
              projectId,
              recordId: event.data.id,
              recordName: event.data.title,
            });
          }
          break;
        }

        case 'project_contracts→bookings': {
          if (event.data?.value > 0) {
            const { count } = await supabase
              .from('bookings')
              .select('id', { count: 'exact', head: true })
              .eq('project_id', projectId)
              .ilike('description', `%${event.data.title || ''}%`);
            if (!count || count === 0) {
              warnings.push({
                id: nextId(),
                module: event.module,
                relatedModule: 'bookings',
                message: `Vertrag "${event.data.title}" hat Wert (${event.data.value}€) aber keine Buchung`,
                severity: 'info',
                action: 'create_booking',
                actionLabel: 'Buchung erstellen',
                projectId,
                recordId: event.data.id,
              });
            }
          }
          break;
        }
      }
    }
  } catch (error) {
    console.error('Cross-check error:', error);
  }

  return warnings;
}
